import {
    ArrowLeft,
    Building,
    CircleMinus,
    CirclePlus,
    ListFilter,
    type LucideIcon,
} from 'lucide-react';
import { Fragment, type ReactElement, useRef, useState } from 'react';

import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger,
} from '@apps/web/components/ui/dropdown_menu';
import { Input } from '@apps/web/components/ui/input';
import { useIsMobile } from '@apps/web/hooks/use_mobile';
import { type FilterMenuSelection } from '@apps/web/lib/filter_selection';
import { cn } from '@apps/web/lib/utils';

// The selection model lives in lib/filter_selection so non-UI code can use
// it; re-exported here so menu consumers import one module.
export {
    EMPTY_FILTER_SELECTION,
    type FilterMenuSelection,
    filterSelectionActive,
    matchesFilterSelection,
    toggledFilterSelection,
} from '@apps/web/lib/filter_selection';

export type FilterMenuOption = {
    readonly value: string;
    readonly label: string;
    readonly sublabel?: string | null;
};

type FilterMenuCategoryBase = {
    readonly id: string;
    readonly title: string;
    readonly icon?: LucideIcon;
    readonly options: ReadonlyArray<FilterMenuOption>;
    readonly maxVisibleOptions?: number;
    readonly onClear: () => void;
};

export type IncludeExcludeFilterMenuCategory = FilterMenuCategoryBase & {
    readonly selectionMode?: 'include-exclude';
    readonly selection: FilterMenuSelection;
    readonly onToggle: (value: string, mode: 'include' | 'exclude') => void;
};

export type IncludeOnlyFilterMenuCategory = FilterMenuCategoryBase & {
    readonly selectionMode: 'include-only';
    readonly selectedValues: ReadonlySet<string>;
    readonly onToggle: (value: string) => void;
};

export type FilterMenuCategory =
    | IncludeExcludeFilterMenuCategory
    | IncludeOnlyFilterMenuCategory;

function selectionSize(category: FilterMenuCategory): number {
    return category.selectionMode === 'include-only'
        ? category.selectedValues.size
        : category.selection.include.size + category.selection.exclude.size;
}

function IncludeOnlyOptionRow({
    category,
    option,
}: {
    readonly category: IncludeOnlyFilterMenuCategory;
    readonly option: FilterMenuOption;
}): ReactElement {
    const isSelected = category.selectedValues.has(option.value);
    return (
        <DropdownMenuCheckboxItem
            data-filter-value={option.value}
            checked={isSelected}
            onCheckedChange={() => {
                category.onToggle(option.value);
            }}
            onSelect={(event) => event.preventDefault()}
        >
            <span className="min-w-0 flex-1 truncate text-sm">
                {option.label}
            </span>
            {option.sublabel ? (
                <span className="text-muted-foreground max-w-24 shrink-0 truncate text-xs">
                    {option.sublabel}
                </span>
            ) : null}
        </DropdownMenuCheckboxItem>
    );
}

function IncludeExcludeOptionRow({
    category,
    option,
}: {
    readonly category: IncludeExcludeFilterMenuCategory;
    readonly option: FilterMenuOption;
}): ReactElement {
    const isIncluded = category.selection.include.has(option.value);
    const isExcluded = category.selection.exclude.has(option.value);
    return (
        <DropdownMenuItem
            className="flex items-center gap-2"
            onSelect={(event) => event.preventDefault()}
        >
            <span className="min-w-0 flex-1 truncate text-sm">
                {option.label}
            </span>
            <span className="flex items-center gap-1">
                <Button
                    type="button"
                    size="icon"
                    variant={isIncluded ? 'default' : 'outline'}
                    className="h-6 w-6"
                    onPointerDown={(event) => event.stopPropagation()}
                    onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        category.onToggle(option.value, 'include');
                    }}
                    aria-label={`Include ${option.label}`}
                >
                    <CirclePlus
                        className={cn(
                            'h-4 w-4',
                            isIncluded ? 'text-white' : undefined,
                        )}
                    />
                </Button>
                <Button
                    type="button"
                    size="icon"
                    variant={isExcluded ? 'destructive' : 'outline'}
                    className="h-6 w-6"
                    onPointerDown={(event) => event.stopPropagation()}
                    onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        category.onToggle(option.value, 'exclude');
                    }}
                    aria-label={`Exclude ${option.label}`}
                >
                    <CircleMinus
                        className={cn(
                            'h-4 w-4',
                            isExcluded ? 'text-white' : undefined,
                        )}
                    />
                </Button>
            </span>
        </DropdownMenuItem>
    );
}

function FilterCategoryLabel({
    category,
}: {
    readonly category: FilterMenuCategory;
}): ReactElement {
    const IconComponent = category.icon ?? Building;
    const count = selectionSize(category);

    return (
        <Fragment>
            <IconComponent className="mr-2 size-4" />
            <span>{category.title}</span>
            {count > 0 ? (
                <Badge
                    variant="secondary"
                    className="bg-primary text-primary-foreground ml-auto rounded-sm px-1.5 py-0.5 text-xs font-semibold"
                >
                    {count}
                </Badge>
            ) : null}
        </Fragment>
    );
}

function FilterCategoryOptionsPanel({
    category,
    search,
    onSearchChange,
}: {
    readonly category: FilterMenuCategory;
    readonly search: string;
    readonly onSearchChange: (value: string) => void;
}): ReactElement {
    const optionsRef = useRef<HTMLDivElement>(null);
    const normalizedSearch = search.trim().toLocaleLowerCase();
    const matchingOptions = normalizedSearch
        ? category.options.filter((option) =>
              option.label.toLocaleLowerCase().includes(normalizedSearch),
          )
        : category.options;
    const selected =
        category.selectionMode === 'include-only'
            ? category.selectedValues
            : category.selection.include;
    const selectedOptions = matchingOptions.filter((option) =>
        selected.has(option.value),
    );
    const unselectedOptions = matchingOptions.filter(
        (option) => !selected.has(option.value),
    );
    const filteredOptions = category.maxVisibleOptions
        ? [...selectedOptions, ...unselectedOptions].slice(
              0,
              category.maxVisibleOptions,
          )
        : matchingOptions;
    const hiddenCount = matchingOptions.length - filteredOptions.length;
    const count = selectionSize(category);

    return (
        <Fragment>
            {category.maxVisibleOptions !== undefined ||
            category.options.length > 5 ? (
                <div className="border-b px-2 pb-2">
                    <Input
                        placeholder={`Search ${category.title.toLowerCase()}…`}
                        value={search}
                        onChange={(event) => onSearchChange(event.target.value)}
                        className="h-8"
                        onClick={(event) => event.stopPropagation()}
                        onKeyDown={(event) => {
                            if (
                                event.key === 'ArrowDown' ||
                                event.key === 'ArrowUp'
                            ) {
                                event.preventDefault();
                                event.stopPropagation();
                                const items = Array.from(
                                    optionsRef.current?.querySelectorAll<HTMLElement>(
                                        '[role="menuitemcheckbox"]:not([data-disabled]), [role="menuitem"]:not([data-disabled])',
                                    ) ?? [],
                                );
                                const item =
                                    event.key === 'ArrowDown'
                                        ? items[0]
                                        : items[items.length - 1];
                                item?.focus();
                            } else if (event.key !== 'Escape') {
                                event.stopPropagation();
                            }
                        }}
                    />
                </div>
            ) : null}
            {filteredOptions.length > 0 ? (
                <div ref={optionsRef} className="max-h-64 overflow-y-auto">
                    {filteredOptions.map((option) => {
                        return category.selectionMode === 'include-only' ? (
                            <IncludeOnlyOptionRow
                                key={option.value}
                                category={category}
                                option={option}
                            />
                        ) : (
                            <IncludeExcludeOptionRow
                                key={option.value}
                                category={category}
                                option={option}
                            />
                        );
                    })}
                    {hiddenCount > 0 ? (
                        <p className="text-muted-foreground px-2 py-1.5 text-xs">
                            {hiddenCount} more — keep typing to narrow
                        </p>
                    ) : null}
                </div>
            ) : (
                <DropdownMenuItem disabled={true}>
                    {search ? 'No matching options' : 'No options available'}
                </DropdownMenuItem>
            )}
            {count > 0 ? (
                <Fragment>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => category.onClear()}>
                        Clear {category.title}
                    </DropdownMenuItem>
                </Fragment>
            ) : null}
        </Fragment>
    );
}

export function IncludeExcludeFilterOptionsPanel({
    category,
}: {
    readonly category: IncludeExcludeFilterMenuCategory;
}): ReactElement {
    const [search, setSearch] = useState('');

    return (
        <FilterCategoryOptionsPanel
            category={category}
            search={search}
            onSearchChange={setSearch}
        />
    );
}

// One category exposed directly in the filter bar (e.g. Office, Assignee).
export function IncludeExcludeFilterCategoryMenu({
    category,
}: {
    readonly category: IncludeExcludeFilterMenuCategory;
}): ReactElement {
    const [search, setSearch] = useState('');
    const IconComponent = category.icon ?? Building;
    const count = selectionSize(category);

    return (
        <DropdownMenu
            onOpenChange={(open) => {
                if (!open) {
                    setSearch('');
                }
            }}
        >
            <DropdownMenuTrigger asChild={true}>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="relative"
                    aria-label={`Filter by ${category.title}`}
                >
                    <IconComponent className="mr-2 h-4 w-4" />
                    <span>{category.title}</span>
                    {count > 0 ? (
                        <Badge
                            variant="secondary"
                            className="bg-primary text-primary-foreground ml-2 rounded-sm px-1.5 py-0.5 text-xs font-semibold"
                        >
                            {count}
                        </Badge>
                    ) : null}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64">
                <DropdownMenuLabel>{category.title}</DropdownMenuLabel>
                <FilterCategoryOptionsPanel
                    category={category}
                    search={search}
                    onSearchChange={setSearch}
                />
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

// Collapsed "Filter" button that opens one submenu per category. Categories
// may use include/exclude controls or a simpler checkbox list. Mirrors the
// shared DataTableToolbar filter popover but works outside a TanStack table.
export function IncludeExcludeFilterMenu({
    categories,
    onClearAll,
}: {
    readonly categories: ReadonlyArray<FilterMenuCategory>;
    readonly onClearAll: () => void;
}): ReactElement {
    const isMobile = useIsMobile();
    const [activeCategoryId, setActiveCategoryId] = useState<string | null>(
        null,
    );
    const activeCategory = isMobile
        ? categories.find((category) => category.id === activeCategoryId)
        : undefined;
    const [searchByCategory, setSearchByCategory] = useState<
        ReadonlyMap<string, string>
    >(new Map());

    const totalSelected = categories.reduce(
        (total, category) => total + selectionSize(category),
        0,
    );

    function setCategorySearch(categoryId: string, value: string): void {
        setSearchByCategory((previous) => {
            const next = new Map(previous);
            if (value) {
                next.set(categoryId, value);
            } else {
                next.delete(categoryId);
            }
            return next;
        });
    }

    return (
        <DropdownMenu
            onOpenChange={(open) => {
                if (!open) {
                    setActiveCategoryId(null);
                    setSearchByCategory(new Map());
                }
            }}
        >
            <DropdownMenuTrigger asChild={true}>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="relative"
                    aria-label="Filter"
                >
                    <ListFilter className="mr-2 h-4 w-4" />
                    <span>Filter</span>
                    {totalSelected > 0 ? (
                        <Badge
                            variant="secondary"
                            className="bg-primary text-primary-foreground ml-2 rounded-sm px-1.5 py-0.5 text-xs font-semibold"
                        >
                            {totalSelected}
                        </Badge>
                    ) : null}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
                {activeCategory ? (
                    <Fragment>
                        <DropdownMenuItem
                            onSelect={(event) => {
                                event.preventDefault();
                                setCategorySearch(activeCategory.id, '');
                                setActiveCategoryId(null);
                            }}
                        >
                            <ArrowLeft className="size-4" />
                            Back to filters
                        </DropdownMenuItem>
                        <DropdownMenuLabel>
                            {activeCategory.title}
                        </DropdownMenuLabel>
                        <FilterCategoryOptionsPanel
                            category={activeCategory}
                            search={
                                searchByCategory.get(activeCategory.id) ?? ''
                            }
                            onSearchChange={(value) =>
                                setCategorySearch(activeCategory.id, value)
                            }
                        />
                    </Fragment>
                ) : (
                    <Fragment>
                        <DropdownMenuLabel>Filter by</DropdownMenuLabel>
                        {categories.map((category) => {
                            const search =
                                searchByCategory.get(category.id) ?? '';

                            if (isMobile) {
                                return (
                                    <DropdownMenuItem
                                        key={category.id}
                                        onSelect={(event) => {
                                            event.preventDefault();
                                            setActiveCategoryId(category.id);
                                        }}
                                    >
                                        <FilterCategoryLabel
                                            category={category}
                                        />
                                    </DropdownMenuItem>
                                );
                            }
                            return (
                                <DropdownMenuSub
                                    key={category.id}
                                    onOpenChange={(open) => {
                                        if (!open) {
                                            setCategorySearch(category.id, '');
                                        }
                                    }}
                                >
                                    <DropdownMenuSubTrigger>
                                        <FilterCategoryLabel
                                            category={category}
                                        />
                                    </DropdownMenuSubTrigger>
                                    <DropdownMenuSubContent className="w-64">
                                        <FilterCategoryOptionsPanel
                                            category={category}
                                            search={search}
                                            onSearchChange={(value) =>
                                                setCategorySearch(
                                                    category.id,
                                                    value,
                                                )
                                            }
                                        />
                                    </DropdownMenuSubContent>
                                </DropdownMenuSub>
                            );
                        })}
                        {totalSelected > 0 ? (
                            <Fragment>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => onClearAll()}>
                                    Clear all
                                </DropdownMenuItem>
                            </Fragment>
                        ) : null}
                    </Fragment>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
